<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useApplicationStore } from '@stores/application';
import { useConfigurationStore } from '@stores/configuration';
import { useAnimationStore } from '@stores/animation';
/***/
import { useAuthUser } from '@compositions/Auth';

// Composants
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const AiCreditsForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/SuperAdmin/AICreditsForm'));
const TaskUrgentForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/SuperAdmin/TaskUrgentForm'));
const RegistrationCreditsForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/SuperAdmin/RegistrationCreditsForm'));
const TranslationForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/SuperAdmin/TranslationForm'));
const AnimationsForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/SuperAdmin/AnimationsForm'));
const TakeMeetUrlForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/SuperAdmin/TakeMeetUrlForm'));
const UpdateSocialTokens = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/SuperAdmin/UpdateSocialTokens'));

const router = useRouter();
const { t } = useI18n();

// Store
const applicationStore = useApplicationStore();
const configurationStore = useConfigurationStore();
const animationStore = useAnimationStore();

// Nombre de crédits pour une tâche en urgence
const taskUrgentCredits = computed(() => configurationStore.getValueById('TASK_URGENT_CREDITS'));
// Nombre de crédits donnés à l'inscription
const registrationCredits = computed(() => configurationStore.getValueById('REGISTRATION_CREDITS'));

const { authUser, authUserIsSuperAdmin } = useAuthUser({});

const currentApplication = computed(() => applicationStore.getCurrent);

const isReady = ref(false);

// Chargement de la configuration
const loadingConfiguration = ref(false);
const errorLoadingConfiguration = ref(false);
const loadConfiguration = (() => {
    if(loadingConfiguration.value) {
        return Promise.reject();
    }

    loadingConfiguration.value = true;
    errorLoadingConfiguration.value = false;

    return configurationStore.loadConfiguration().then(() => {
        return Promise.resolve();
    }).catch(() => {
        errorLoadingConfiguration.value = true;
        return Promise.reject();
    }).finally(() => {
        loadingConfiguration.value = false;
    });
});

// Chargement des animations
const loadingAnimations = ref(false);
const errorLoadingAnimation = ref(false);
const loadAnimations = (() => {
    if(loadingAnimations.value) {
        return false;
    }

    loadingAnimations.value = true;
    errorLoadingAnimation.value = false;

    return animationStore.loadAnimations().then(() => {
        // Géré par le store
    }).catch(() => {
        errorLoadingAnimation.value = true;
    }).finally(() => {
        loadingAnimations.value = false;
    });
});

onMounted(() => {
    if(! authUserIsSuperAdmin.value) {
        router.push({
            name: 'error',
            params: {
                statusCode: 404
            }
        });
    } else {
        isReady.value = true;
        document.title = t('page.admin.superAdmin.settings.title') + ' - ' + currentApplication.value.name;
        loadConfiguration().then(() => {
            loadAnimations();
        });
    }
});

</script>

<template>
    <layout v-if="isReady">
        <div class="content_header">
            <div class="content-header_title">
                <h1 class="first-title">{{ $t('page.admin.superAdmin.settings.title') }}</h1>
                <p>{{ $t('page.admin.superAdmin.settings.description') }}</p>
            </div>
            <div class="content-header_btn">
                <router-link
                    :to="{ name: 'admin.super-admin.ai-test' }"
                    :title="$t('page.admin.superAdmin.settings.buttons.aiTest.altLabel')"
                    class="button button-primary-outline"
                >
                    <span>{{ $t('page.admin.superAdmin.settings.buttons.aiTest.label') }}</span>
                </router-link>
            </div>
        </div>
        <loader v-if="loadingConfiguration || loadingAnimations"></loader>
        <p
            v-else-if="errorLoadingConfiguration || errorLoadingAnimation"
            class="generic-message generic-message-error"
        >
            {{ $t('form.admin.superAdmin.configuration.loading.error') }}
        </p>

        <template v-else>
            <update-social-tokens></update-social-tokens>
            <take-meet-url-form></take-meet-url-form>
            <ai-credits-form></ai-credits-form>
            <animations-form></animations-form>
            <task-urgent-form v-if="taskUrgentCredits !== null"></task-urgent-form>
            <registration-credits-form v-if="registrationCredits !== null"></registration-credits-form>
            <translation-form></translation-form>
        </template>
    </layout>
</template>
